import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Grid,
  Tooltip,
  CircularProgress,
  Modal
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import api, { API_BASE_URL } from "../../../../utils/api";
import axios from "axios";

const RequestFilesViewer = ({ requestId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);

  const buildFileUrl = (filePath) => `${API_BASE_URL}${filePath}`;

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await api.get(`/request-files/${requestId}`);
        setFiles(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch request files", err);
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchFiles();
    }
  }, [requestId]);

  const getIcon = (type) =>
    type?.startsWith("image/") ? <ImageIcon color="primary" /> : <DescriptionIcon color="action" />;

  const handleDownload = (file) => {
    const url = buildFileUrl(file.file_url);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", file.file_name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  

  if (loading) return <CircularProgress sx={{ mt: 2 }} />;
  if (!files.length) return <Typography variant="body2" sx={{ mt: 2 }}>No files uploaded for this request.</Typography>;

  return (
    <Box mt={3}>
      <Typography variant="h6" gutterBottom>Uploaded Files</Typography>
      <Grid container spacing={2}>
        {files.map((file) => {
          return (
            <Grid item xs={12} sm={6} md={4} key={file.id}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {getIcon(file.file_type)}
                    <Typography variant="body2" noWrap>{file.file_name}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">{file.file_type}</Typography>
                </CardContent>
                <CardActions>
                  <Tooltip title="Preview">
                    <IconButton
                      onClick={() => setPreviewFile(file)}
                      aria-label="Preview file"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download">
  <IconButton
    onClick={() => handleDownload(file)}
    aria-label="Download file"
  >
    <DownloadIcon />
  </IconButton>
</Tooltip>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Preview Modal */}
      <Modal
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        aria-labelledby="preview-title"
        aria-describedby="preview-body"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: 900,
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: "auto"
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" id="preview-title">
              {previewFile?.file_name}
            </Typography>
            <IconButton onClick={() => setPreviewFile(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {previewFile?.file_type?.startsWith("image/") ? (
            <img
              src={buildFileUrl(previewFile.file_url)}
              alt={previewFile.file_name}
              style={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
            />
          ) : previewFile?.file_type === "application/pdf" ? (
            <iframe
              src={buildFileUrl(previewFile.file_url)}
              title="PDF Preview"
              width="100%"
              height="600px"
              style={{ border: "none" }}
            />
          ) : previewFile?.file_type === "text/plain" ? (
            <iframe
              src={buildFileUrl(previewFile.file_url)}
              title="Text Preview"
              width="100%"
              height="600px"
              style={{ border: "1px solid #ddd", backgroundColor: "#f5f5f5" }}
            />
          ) : previewFile?.file_type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
            previewFile?.file_name?.toLowerCase().endsWith(".docx") ? (
            <Typography variant="body2" color="text.secondary">
              DOCX preview is not supported directly.{" "}
              <a
                href={buildFileUrl(previewFile.file_url)}
                download={previewFile.file_name}
              >
                Download the file
              </a>{" "}
              to view it.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Cannot preview this file type. Please download it to view.
            </Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default RequestFilesViewer;
